import React from "react";
import {
  Typography,
  List,
  Tag,
  Select,
  Text,
  Checkbox,
  Tooltip,
  Space,
  InputNumber,
  Badge,
} from "antd";
import { useTranslation } from "react-i18next";
import { InfoCircleOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useBookingWizard } from "../../../contexts/BookingWizardContext";
import { formatEuro, getTransportTypeColor } from "../PricingCalculatorHelpers";
import {
  EXPORT_CLEARANCE_LICENSE,
  PICKUP_FEE,
} from "../../../lib/pricingDefaults";
import {
  RouteOption,
  PickupRequest,
  BookingSupplier,
} from "../PricingCalculatorTypes";
import { Tariff } from "../../../hooks/useTariffs";
import { getStatusColor } from "../PricingCalculatorHelpers";

const { Text: AntText } = Typography;

interface BookingSummarySectionProps {
  transportType: string;
  incoterm: string;
  routeOptions: RouteOption[];
  selectedOriginPortId: number | null;
  selectedDestinationPortId: number | null;
  handleRouteChange: (value: string) => void;
  applicableTariff: Tariff | null;
  bookingSupplier: BookingSupplier | null;
  handleExportClearanceLicenseChange: (checked: boolean) => void;
  handlePickupFeeChange: (value: number | null) => void;
  pickupRequest: PickupRequest | null;
  showPickupDetails: () => void;
  openPickupRequestModal: () => void;
  getStatusText: (status: string) => string;
}

export const BookingSummarySection: React.FC<BookingSummarySectionProps> = ({
  transportType,
  incoterm,
  routeOptions,
  selectedOriginPortId,
  selectedDestinationPortId,
  handleRouteChange,
  applicableTariff,
  bookingSupplier,
  handleExportClearanceLicenseChange,
  handlePickupFeeChange,
  pickupRequest,
  showPickupDetails,
  openPickupRequestModal,
  getStatusText,
}) => {
  const { state } = useBookingWizard();
  const { t } = useTranslation();

  const shouldShowPickupFee = () => {
    return (
      state.currentStep >= PICKUP_FEE.DISPLAY_FROM_STEP &&
      state.booking_incoterm === 1
    );
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <AntText strong>
        {t("bookingWizard.bookingSummary", "Booking Summary")}
      </AntText>
      <List size="small" style={{ marginTop: 8 }} itemLayout="horizontal">
        {transportType && (
          <List.Item
            style={{ display: "flex", justifyContent: "space-between" }}
          >
            <AntText>
              {t(
                "bookingWizard.transportDetails.transportType",
                "Transport Type"
              )}
            </AntText>
            <Tag color={getTransportTypeColor(state.transport_type)}>
              {transportType}
            </Tag>
          </List.Item>
        )}

        {incoterm && (
          <List.Item
            style={{ display: "flex", justifyContent: "space-between" }}
          >
            <AntText>
              {t("bookingWizard.transportDetails.incoterm", "Incoterm")}
            </AntText>
            <Tag>{incoterm}</Tag>
          </List.Item>
        )}

        {/* Export Clearance License Option (not for FOB incoterm) */}
        {state.booking_incoterm !== 3 && (
          <List.Item
            style={{ display: "flex", justifyContent: "space-between" }}
          >
            <Checkbox
              checked={state.includeExportClearanceLicense}
              onChange={(e) =>
                handleExportClearanceLicenseChange(e.target.checked)
              }
            >
              <span>{t("includeExportClearanceLicense")}</span>
              <AntText type="secondary" style={{ marginLeft: 8 }}>
                {formatEuro(EXPORT_CLEARANCE_LICENSE.BASE_FEE)}
              </AntText>
            </Checkbox>
          </List.Item>
        )}

        {/* Pickup Fee Input (only for EXW incoterm) */}
        {shouldShowPickupFee() && (
          <List.Item
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              <Space>
                <AntText>{t("pickupFee")}</AntText>
                <Tooltip title={t("pickupFeeTooltip")}>
                  <InfoCircleOutlined />
                </Tooltip>
              </Space>
              <div>
                {pickupRequest ? (
                  <Typography.Link
                    onClick={showPickupDetails}
                    style={{
                      fontSize: "12px",
                      display: "inline-flex",
                      alignItems: "center",
                    }}
                  >
                    <Badge
                      status={getStatusColor(pickupRequest.status)}
                      text={getStatusText(pickupRequest.status)}
                      style={{ fontSize: "12px" }}
                    />
                  </Typography.Link>
                ) : (
                  <Typography.Link
                    onClick={openPickupRequestModal}
                    style={{ fontSize: "12px" }}
                  >
                    <QuestionCircleOutlined style={{ marginRight: 4 }} />
                    {t("requestPickupFeeQuote")}
                  </Typography.Link>
                )}
              </div>
            </div>
            {pickupRequest && pickupRequest.cost ? (
              <AntText strong>{formatEuro(pickupRequest.cost)}</AntText>
            ) : (
              <InputNumber
                value={state.pickupFee || 0}
                onChange={handlePickupFeeChange}
                min={0}
                step={1}
                precision={2}
                prefix="€"
                style={{ width: 120 }}
              />
            )}
          </List.Item>
        )}

        {/* Route Selection */}
        <List.Item
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <AntText>
            {t("bookingWizard.transportDetails.route", "Route")}
          </AntText>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
            }}
          >
            <Select
              style={{ width: 280 }}
              value={`${selectedOriginPortId}-${selectedDestinationPortId}`}
              onChange={handleRouteChange}
              placeholder={t(
                "bookingWizard.transportDetails.selectRoute",
                "Select route"
              )}
              options={routeOptions}
              disabled={!state.transport_type}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
            {applicableTariff && applicableTariff.from && (
              <AntText
                type="secondary"
                style={{ fontSize: "12px", marginTop: "4px" }}
              >
                {t(
                  "bookingWizard.pricingCalculator.tariffValidityPeriod",
                  "Valid"
                )}
                : {dayjs(applicableTariff.from).format("DD/MM/YYYY")} -{" "}
                {applicableTariff.to
                  ? dayjs(applicableTariff.to).format("DD/MM/YYYY")
                  : t("common.unlimited")}
              </AntText>
            )}
          </div>
        </List.Item>

        {state.origin_pickup_date && (
          <List.Item
            style={{ display: "flex", justifyContent: "space-between" }}
          >
            <AntText>
              {t(
                "bookingWizard.transportDetails.cargoReadinessDate",
                "Cargo Readiness"
              )}
            </AntText>
            <AntText>
              {dayjs(state.origin_pickup_date).format("DD/MM/YYYY")}
            </AntText>
          </List.Item>
        )}

        {state.products && state.products.length > 0 && (
          <List.Item
            style={{ display: "flex", justifyContent: "space-between" }}
          >
            <AntText>{t("bookingWizard.productsSummary", "Products")}</AntText>
            <Tag color="green">{state.products.length}</Tag>
          </List.Item>
        )}

        {bookingSupplier && (
          <List.Item
            style={{ display: "flex", justifyContent: "space-between" }}
          >
            <AntText>
              {t(
                "bookingWizard.transportDetails.supplierLocation",
                "Supplier Location"
              )}
            </AntText>
            <Tag color="geekblue">{`${bookingSupplier.city}, ${bookingSupplier.country}`}</Tag>
          </List.Item>
        )}
      </List>
    </div>
  );
};















